import { type CurrenciesType } from '@/shared/schema';

export interface PaymentPresetField {
  name: string;
  placeholder: string;
  required: boolean;
  inputType?: 'text' | 'number' | 'email' | 'tel';
}

export interface PaymentPreset {
  id: string;
  label: string;
  icon: string;
  type: 'cash' | 'transfer' | 'zelle' | 'crypto' | 'other';
  currency: CurrenciesType;
  fields: PaymentPresetField[];
  supportedCurrencies?: CurrenciesType[]; // For filtering in UI
}

export const PAYMENT_PRESETS: PaymentPreset[] = [
  {
    id: 'pago-movil',
    label: 'Pago Móvil',
    icon: 'Smartphone',
    type: 'transfer',
    currency: 'BS',
    supportedCurrencies: ['BS'],
    fields: [
      {
        name: 'Teléfono',
        placeholder: '0412-1234567',
        required: true,
        inputType: 'tel',
      },
      { name: 'Cédula', placeholder: 'V-12345678', required: true },
      {
        name: 'Banco',
        placeholder: 'Banesco, Mercantil, etc.',
        required: true,
      },
    ],
  },
  {
    id: 'nequi',
    label: 'Nequi',
    icon: 'Smartphone',
    type: 'transfer',
    currency: 'COP',
    supportedCurrencies: ['COP'],
    fields: [
      {
        name: 'Teléfono / Celular',
        placeholder: '300 123 4567',
        required: true,
        inputType: 'tel',
      },
      { name: 'Nombre', placeholder: 'Titular de la cuenta', required: true },
    ],
  },
  {
    id: 'daviplata',
    label: 'Daviplata',
    icon: 'Smartphone',
    type: 'transfer',
    currency: 'COP',
    supportedCurrencies: ['COP'],
    fields: [
      {
        name: 'Teléfono / Celular',
        placeholder: '300 123 4567',
        required: true,
        inputType: 'tel',
      },
      { name: 'Nombre', placeholder: 'Titular de la cuenta', required: true },
    ],
  },
  {
    id: 'yape',
    label: 'Yape',
    icon: 'Smartphone',
    type: 'transfer',
    currency: 'PEN',
    supportedCurrencies: ['PEN'],
    fields: [
      {
        name: 'Teléfono / Celular',
        placeholder: '912 345 678',
        required: true,
        inputType: 'tel',
      },
      { name: 'Nombre', placeholder: 'Titular de la cuenta', required: true },
    ],
  },
  {
    id: 'plin',
    label: 'Plin',
    icon: 'Smartphone',
    type: 'transfer',
    currency: 'PEN',
    supportedCurrencies: ['PEN'],
    fields: [
      {
        name: 'Teléfono / Celular',
        placeholder: '912 345 678',
        required: true,
        inputType: 'tel',
      },
      { name: 'Nombre', placeholder: 'Titular de la cuenta', required: true },
    ],
  },
  {
    id: 'efectivo-bs',
    label: 'Efectivo en Bolívares',
    icon: 'Banknote',
    type: 'cash',
    currency: 'BS',
    supportedCurrencies: ['BS'],
    fields: [],
  },
  {
    id: 'efectivo-usd',
    label: 'Efectivo en Dólares',
    icon: 'DollarSign',
    type: 'cash',
    currency: 'USD',
    supportedCurrencies: ['USD', 'BS', 'COP', 'PEN', 'MXN', 'ARS', 'CLP', 'UYU'],
    fields: [],
  },
  {
    id: 'zelle',
    label: 'Zelle',
    icon: 'Zap',
    type: 'zelle',
    currency: 'USD',
    supportedCurrencies: ['USD', 'BS', 'COP', 'PEN'],
    fields: [
      {
        name: 'Email o Teléfono',
        placeholder: 'email@example.com o 0412-1234567',
        required: true,
      },
    ],
  },
  {
    id: 'transferencia',
    label: 'Transferencia Bancaria',
    icon: 'Landmark',
    type: 'transfer',
    currency: 'BS',
    supportedCurrencies: ['BS'],
    fields: [
      {
        name: 'Banco',
        placeholder: 'Banesco, Mercantil, etc.',
        required: true,
      },
      {
        name: 'Número de Cuenta',
        placeholder: '0134-1234-56-1234567890',
        required: true,
      },
      { name: 'Cédula', placeholder: 'V-12345678', required: true },
      { name: 'Titular', placeholder: 'Nombre del titular', required: false },
    ],
  },
  {
    id: 'transferencia-inter',
    label: 'Transferencia Local',
    icon: 'Landmark',
    type: 'transfer',
    currency: 'USD',
    supportedCurrencies: ['COP', 'PEN', 'MXN', 'ARS', 'CLP', 'UYU'],
    fields: [
      { name: 'Banco', placeholder: 'Nombre del banco', required: true },
      {
        name: 'Número de Cuenta',
        placeholder: 'Número de cuenta o CLABE',
        required: true,
      },
      { name: 'Titular', placeholder: 'Nombre del titular', required: true },
      { name: 'Documento ID', placeholder: 'RUT, NIT, DNI, etc.', required: false },
    ],
  },
  {
    id: 'binance',
    label: 'Binance Pay',
    icon: 'Bitcoin',
    type: 'crypto',
    currency: 'USD',
    supportedCurrencies: ['USD', 'BS', 'COP', 'PEN', 'MXN'],
    fields: [
      { name: 'ID de Binance Pay', placeholder: '123456789', required: true },
    ],
  },
  {
    id: 'paypal',
    label: 'PayPal',
    icon: 'CreditCard',
    type: 'other',
    currency: 'USD',
    supportedCurrencies: ['USD', 'BS', 'COP', 'PEN', 'MXN'],
    fields: [
      {
        name: 'Email de PayPal',
        placeholder: 'email@example.com',
        required: true,
        inputType: 'email',
      },
    ],
  },
];

export const getPresetById = (id: string): PaymentPreset | undefined => {
  return PAYMENT_PRESETS.find((p) => p.id === id);
};

export const getPresetByTitle = (title: string): PaymentPreset | undefined => {
  return PAYMENT_PRESETS.find(
    (p) => p.label.toLowerCase() === title.toLowerCase(),
  );
};

export const getFieldHelperText = (fieldName: string): string => {
  const helpers: Record<string, string> = {
    Teléfono: 'Formato: 0412-1234567',
    Cédula: 'Formato: V-12345678 o E-12345678',
    Banco: 'Ej: Banesco, Mercantil, Venezuela',
    'Email o Teléfono': 'El email o teléfono registrado en Zelle',
    'Número de Cuenta': 'Número de cuenta bancaria (20 dígitos)',
    Titular: 'Nombre completo del titular de la cuenta',
    'ID de Binance Pay': 'Tu ID numérico de Binance Pay',
    'Email de PayPal': 'Email asociado a tu cuenta PayPal',
  };
  return helpers[fieldName] || '';
};
